import { api } from "../scripts/api.js";

async function apiJson(path, init) {
   const res = await api(path, init);
   const data = await res.json().catch(() => null);
   if (!res.ok || data?.ok === false) {
      const err = new Error(data?.reason || res.statusText || "request_failed");
      err.status = res.status;
      throw err;
   }
   return data;
}
const esc = (s) =>
   String(s ?? "").replace(/[&<>"']/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const fmtTime = (ts) => {
   const match = String(ts ?? "").match(/(\d{2}:\d{2}:\d{2})/);
   return match ? match[1] : "--:--:--";
}

const DASH = `<span class="act-dash">-</span>`;

const COLUMNS = [
   { key: "client_timestamp", label: "Time", cell: (e) => `<span class="act-mono act-time">${esc(fmtTime(e.client_timestamp))}</span>` },
   { key: "worksheet_name", label: "Sheet", cell: (e) => (e.worksheet_name ? esc(e.worksheet_name) : DASH) },
   { key: "event_type", label: "Event", cell: (e) => `<span class="act-badge" data-event="${esc(e.event_type)}">${esc(e.event_type)}</span>` },
   { key: "cell_address", label: "Cell", cell: (e) => (e.cell_address ? `<span class="act-cell">${esc(e.cell_address)}</span>` : DASH) },
   { key: "current_value", label: "Value", cell: (e) => (e.current_value ? `<span class="act-mono">${esc(e.current_value)}</span>` : DASH) },
   { key: "formula_current", label: "Formula", cell: (e) => (e.formula_current ? `<span class="act-mono act-formula">${esc(e.formula_current)}</span>` : DASH) },
   { key: "client_seq", label: "Seq", cell: (e) => `<span class="act-mono act-seq">${esc(e.client_seq)}</span>` },
];

const options = (rows, idOf, labelOf, placeholder) =>
   `<option value="">${esc(placeholder)}</option>` +
   rows.map((r) => `<option value="${esc(idOf(r))}">${esc(labelOf(r))}</option>`).join("");

class Activity extends HTMLElement {
   connectedCallback() {
      const params = new URLSearchParams(location.search);
      this.state = {
         classes: [],
         students: [],
         assignments: [],
         classId: params.get("class") || "",
         studentId: params.get("student") || "",
         assignmentId: params.get("assignment") || "",
         events: [],
         status: "idle",
         error: "",
      }

      this.innerHTML = `
         <section>
            <header>
               <h1>Student Activity</h1>
            </header>
            
            <div class="act-controls">
               <label class="act-field">
                  <span>Class</span>
                  <select data-class></select>
               </label>
               <label class="act-field" data-student-field hidden>
                  <span>Student</span>
                  <select data-student></select>
               </label>

               <label class="act-field" data-assignment-field hidden>
                  <span>Assignment</span>
                  <select data-assignment></select>
               </label>
            </div>

            <div data-log></div>
         </section>
      `;

      this.els = {
         class: this.querySelector("[data-class]"),
         student: this.querySelector("[data-student]"),
         assignment: this.querySelector("[data-assignment]"),
         studentField: this.querySelector("[data-student-field]"),
         assignmentField: this.querySelector("[data-assignment-field]"),
         log: this.querySelector("[data-log]"),
      }

      this.onClassChange = () => {
         this.state.classId = this.els.class.value;
         this.state.studentId = "";
         this.state.assignmentId = "";
         this.state.students = [];
         this.state.assignments = [];
         this.state.events = [];
         this.state.status = "idle";
         this.renderChildFilters(!!this.state.classId);
         this.renderLog();
         this.syncUrl();
         this.loadOptions();
      }

      this.onFilterChange = (e) => {
         const key = e.target === this.els.student ? "studentId" : "assignmentId";
         this.state[key] = e.target.value;
         this.syncUrl();
         this.loadEvents();
      }

      this.els.class.addEventListener("change", this.onClassChange);
      this.els.student.addEventListener("change", this.onFilterChange);
      this.els.assignment.addEventListener("change", this.onFilterChange);

      this.loadClasses();
   }

   disconnectedCallback() {
      this.els?.class.removeEventListener("change", this.onClassChange);
      this.els?.student.removeEventListener("change", this.onFilterChange);
      this.els?.assignment.removeEventListener("change", this.onFilterChange);
   }

   async loadClasses() {
      try {
         const { classes = [] } = await apiJson("/activity/classes");
         this.state.classes = classes;

         const linked = classes.some((c) => String(c.class_id) === this.state.classId);
         if (!linked) {
            this.state.classId = "";
         }

         this.els.class.innerHTML = options(
            classes,
            (c) => c.class_id,
            (c) => c.class_name,
            "Choose a class",
         );
         this.els.class.value = this.state.classId;

         if (!classes.length) {
            this.state.status = "no-classes";
            return this.renderLog();
         }
         this.syncUrl();
         if (this.state.classId) {
            await this.loadOptions();
         } else {
            this.renderLog();
         }
      } catch (err) {
         this.fail(err, "Couldn't load your classes.");
      }
   }

   async loadOptions() {
      const { classId } = this.state;
      if (!classId) return;

      const token = Symbol("options");
      this.pendingOptions = token;

      try {
         const query = new URLSearchParams({ classId });
         const { students = [], assignments = [] } = await apiJson(`/activity/options?${query}`);
         if (this.pendingOptions !== token) return;

         this.state.students = students;
         this.state.assignments = assignments;

         if (!students.some((s) => String(s.user_id) === this.state.studentId)) {
            this.state.studentId = "";
         }
         if (!assignments.some((a) => String(a.assignment_id) === this.state.assignmentId)) {
            this.state.assignmentId = "";
         }

         this.renderChildFilters();
         this.syncUrl();
         await this.loadEvents();
      } catch (err) {
         if (this.pendingOptions !== token) return;
         this.fail(err, "Couldn't load the students and assignments for this class.");
      }
   }

   async loadEvents() {
      const { classId, studentId, assignmentId } = this.state;
      if (!classId || !studentId || !assignmentId) {
         this.state.status = "idle";
         return this.renderLog();
      }

      this.state.status = "loading";
      this.renderLog();

      const token = Symbol("events");
      this.pendingEvents = token;

      try {
         const query = new URLSearchParams({ classId, studentId, assignmentId });
         const data = await apiJson(`/activity?${query}`);
         if (this.pendingEvents !== token) return;
         this.state.events = data.events || [];
         this.state.status = this.state.events.length ? "ready" : "empty";
         this.renderLog();
      } catch (err) {
         if (this.pendingEvents !== token) return;
         this.fail(err, "Couldn't load this activity log.");
      }
   }

   fail(err, message) {
      console.error(err);
      this.state.status = "error";
      this.state.error = message;
      this.renderLog();
   }

   syncUrl() {
      const query = new URLSearchParams(location.hash);
      for (const [param, value] of [
         ["class", this.state.classId],
         ["student", this.state.studentId],
         ["assignment", this.state.assignmentId]
      ]) {
         if (value) query.set(param, value);
         else query.delete(param);
      }
      history.replaceState(null, "", `${location.pathname}?${query}`);
   }

   renderChildFilters(loading = false) {
      const { students, assignments, studentId, assignmentId, classId } = this.state;
      const hasClass = !!classId;

      this.els.studentField.hidden = !hasClass;
      this.els.assignmentField.hidden = !hasClass;


      this.els.student.innerHTML = options(
         students,
         (s) => s.user_id,
         (s) => s.name,
         loading ? "Loading..." : students.length ? "Choose a student" : "No students enrolled"
      );
      this.els.assignment.innerHTML = options(
         assignments,
         (a) => a.assignment_id,
         (a) => a.title,
         loading ? "Loading..." : assignments.length ? "Choose an assignment" : "No assignments yet",
      );

      this.els.student.value = studentId;
      this.els.assignment.value = assignmentId;
      this.els.student.disabled = loading || !students.length;
      this.els.assignment.disabled = loading || !assignments.length;
   }

   renderLog() {
      const { status, error, events } = this.state;

      if (status === "loading") {
         this.els.log.innerHTML = `<p class="act-note" aria-busy="true">Loading activity...</p>`;
         return;
      }

      if (status === "error") {
         this.els.log.innerHTML = `
            <p class="act-note act-note--error">
               ${esc(error)} <button type="button" class="act-retry">Try again</button>
            </p>
         `;
         this.els.log.querySelector(".act-retry").addEventListener("click", () => this.loadEvents(), { once: true });
         return;
      }

      if (status === "no-classes") {
         this.els.log.innerHTML = `<p class="act-note">You aren't teaching any classes yet.</p>`;
         return;
      }

      if (status === "idle") {
         this.els.log.innerHTML = `<p class="act-note">Pick a class, student and assignment to see the activity log.</p>`;
         return;
      }

      if (status === "empty") {
         this.els.log.innerHTML = `<p class="act-note">No activity yet. Events appear here as soon as the student opens the workbook.</p>`;
         return;
      }

      this.els.log.innerHTML = `
         <div class="act-log">
            <table class="act-table">
               <thead>
                  <tr>${COLUMNS.map((c) => `<th scope="col">${c.label}</th>`).join("")}</tr>
               </thead>
               <tbody>${events.map((e) => this.row(e)).join("")}</tbody>
            </table>
         </div>
      `;
   }

   row(e) {
      return `<tr>${COLUMNS.map((c) => `<td>${c.cell(e)}</td>`).join("")}</tr>`;
   }
}

customElements.define("app-activity", Activity);